import React, {Fragment, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import ProductInfo from 'pages/ProductOrderPages/ProductOrderList/Components/ProductInfo'
import {Button, makeStyles} from '@material-ui/core'
import ProductsOrderStyle from 'pages/ProductOrderPages/style'
import useProductManagement from 'features/productManagement/useProductManagement'
import useOrganization from 'features/org/useOrganization'
import {
  PAYMENT_METHOD,
  TYPE_INDICATOR_PRODUCT,
  UNIT_PRICES,
} from 'pages/ProductsPages/constant'
import AppLoading from 'components/atoms/Loading'
import {
  fetchProductDetailUserApi,
  ProductAvailableType,
  ProductUserDetailResponse,
} from 'api/productApi'
import {LIST_TABS_RECHARGE} from 'pages/ProductRegistrationOrganization/constant'
import {formatPriceNumber} from 'helpers/uiHelper'
import {usdToCent} from 'helpers/paymentHelper'
import {simpleSanitizeHTML} from 'helpers/analysisHelper'
import UserGuideDialog from 'components/Dialog/Product/UserGuideDialog'

const useStyles = makeStyles(ProductsOrderStyle)
type ProductDataUnion = ProductUserDetailResponse | ProductAvailableType

const KOREAN_COUNTRY_CODE = 'KR'

const ProductOrderList = () => {
  const {t, i18n} = useTranslation()
  const [isShowUserGuide, setIsShowUserGuide] = useState(false)
  const classes = useStyles()
  const {organization} = useOrganization()
  const seen = new Set<string>()

  const {
    listProductAvailableLoading,
    listProductInquiryLoading,
    listProductAvailable,
    productInquiry,
    handleGetListProductAvailable,
    getPackageList,
    handleGetListProductInquiry,
    getNameProduct,
  } = useProductManagement()

  const [activeTabs, setActiveTabs] = useState<string>(
    LIST_TABS_RECHARGE[0].value,
  )

  const listNoChargeProduct = useMemo(() => {
    const filteredList = listProductAvailable
      ?.filter((item) => item.sale.paymentMethod !== PAYMENT_METHOD.MANUAL)
      .sort((a, b) => {
        if (a.goods.orderRank < b.goods.orderRank) return -1
        if (a.goods.orderRank > b.goods.orderRank) return 1
        return 0
      })
    return filteredList?.map((item) => ({
      ...item,
      sale: {
        ...item.sale,
        canCharge: false,
      },
    }))
  }, [listProductAvailable])

  const getData = async () => {
    try {
      await handleGetListProductAvailable()
    } catch (e) {
      console.log(e)
    }
  }
  useEffect(() => {
    getData()
    handleGetListProductInquiry()
    getPackageList({
      target: TYPE_INDICATOR_PRODUCT.ALL,
    })
  }, [])
  const NoProduct = () => {
    return (
      <div className={classes.noProduct}>
        <div className={classes.noProductsContainer}>
          <div className={classes.noProductsIcon}>📦</div>
          <div className={classes.noProductsTitle}>{t('INoProduct')}</div>
          <div className={classes.noProductsSubtitle}>
            {t('INoProductSubtitle')}
          </div>
        </div>
      </div>
    )
  }
  const [productDataMap, setProductDataMap] = useState<Record<string, any>>({})

  useEffect(() => {
    const fetchAll = async () => {
      const promises =
        productInquiry?.flatMap(async (item) => {
          const key = item.goodsId
          const res = await fetchProductDetailUserApi({goodsId: item.goodsId})
          return [key, res.data]
        }) ?? []

      const promiseResults = await Promise.allSettled(promises)

      const results = promiseResults
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value)

      const newMap = results.reduce((acc, [key, data]) => {
        acc[key as any] = data
        return acc
      }, {} as Record<string, any>)

      setProductDataMap(newMap)
    }
    fetchAll()
  }, [productInquiry])

  const extractProductInfo = (data: ProductDataUnion) => {
    const targetCurrency =
      organization?.country.code === KOREAN_COUNTRY_CODE
        ? UNIT_PRICES.KRW
        : UNIT_PRICES.USD

    const salePrice =
      data.sale.salePrice.find(
        (price: {currency: string; price: number}) =>
          price.currency === targetCurrency,
      )?.price ?? 0

    const originalPrice =
      data.sale.price.find((price) => price.currency === targetCurrency)
        ?.price ?? 0

    const amountProduct =
      targetCurrency === UNIT_PRICES.USD ? usdToCent(salePrice) : salePrice

    const amountOriginalProduct =
      targetCurrency === UNIT_PRICES.USD
        ? usdToCent(originalPrice)
        : originalPrice

    return {
      goodsId: data.goods.goodsId,
      goodsName: getNameProduct(data.goods.goodsName).name || '',
      description: getNameProduct(data.goods.goodsName).description || '',
      paymentMethod: data.sale.paymentMethod,
      productSale: data.sale,
      amountProduct: `${targetCurrency} ${formatPriceNumber(amountProduct)}`,
      amountOriginalProduct: `${targetCurrency} ${formatPriceNumber(
        amountOriginalProduct,
      )}`,
      Duration: data.offer.usagePeriod,
      offerType: data.offer.offerType,
      canCharge: data.sale.canCharge,
      offerItems: data.offer.offerItems.map((item) => ({
        ...item,
        unitPrices: item.unitPrices.map((price) => ({
          ...price,
          price:
            price.currency === UNIT_PRICES.USD
              ? usdToCent(price.price)
              : price.price,
        })),
      })),
      saleTarget: data.sale.saleTarget,
    }
  }

  const listProduct = useMemo(() => {
    if (!productInquiry) return []
    return [...productInquiry].sort(
      (a, b) =>
        new Date(a.deactivationDateTime).getTime() -
        new Date(b.deactivationDateTime).getTime(),
    )
  }, [productInquiry])

  const countPurchased = (goodsId: string) => {
    return listProduct.filter((item) => item.goodsId === goodsId).length
  }

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 4,
        padding: '18px 57px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {i18n.language === 'en' && (
        <Button
          color='primary'
          variant='contained'
          onClick={() => setIsShowUserGuide(true)}
          style={{width: '120px', marginLeft: 'auto'}}
        >
          User Guide
        </Button>
      )}
      <div className={classes.tabsUnderline}>
        {LIST_TABS_RECHARGE.map((item) => (
          <div key={item.value}>
            <Button
              onClick={() => setActiveTabs(item.value)}
              className={`${classes.tabUnderline} ${
                activeTabs === item.value ? classes.activeTabs : ''
              }`}
            >
              {t(item.label)}
            </Button>
            {activeTabs === item.value && <div className={classes.active} />}
          </div>
        ))}
        <div className={classes.underlineIndicator} />
      </div>
      <AppLoading
        loading={listProductAvailableLoading || listProductInquiryLoading}
      />

      {organization?.country.code !== KOREAN_COUNTRY_CODE && (
        <div
          style={{
            marginBottom: 20,
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: simpleSanitizeHTML(t('IListProductNote')),
          }}
        />
      )}

      {productInquiry?.length || listNoChargeProduct?.length ? (
        <div className={classes.listCard}>
          {listProduct
            .filter(
              (product) =>
                productDataMap[product.goodsId]?.offer.offerType ===
                  activeTabs &&
                productDataMap[product.goodsId]?.sale.paymentMethod !==
                  PAYMENT_METHOD.MANUAL &&
                new Date().getTime() >= product.activationDateTime,
            )
            .map(({goodsId, id, deactivationDateTime, items}) => {
              const product = productDataMap[goodsId]
              if (!product) return null

              const isDuplicate = seen.has(goodsId)
              seen.add(goodsId)
              return (
                <div key={id}>
                  <ProductInfo
                    Info={extractProductInfo(product)}
                    isOrdered={goodsId in productDataMap}
                    deactivateDate={new Date(deactivationDateTime as number)}
                    licenseItems={items}
                    licenseId={id}
                    isDuplicate={isDuplicate}
                    countPurchased={countPurchased(goodsId)}
                  />
                </div>
              )
            })}
          {listNoChargeProduct
            ?.filter(
              (product) =>
                product.offer.offerType === activeTabs &&
                product.sale.paymentMethod !== PAYMENT_METHOD.MANUAL &&
                !seen.has(product.goods.goodsId),
            )
            .map((item) => (
              <ProductInfo
                key={item.goods.goodsId}
                Info={extractProductInfo(item)}
                isOrdered={false}
                deactivateDate={
                  new Date(
                    productInquiry?.find(
                      (inquiry) => inquiry.goodsId === item.goods.goodsId,
                    )?.deactivationDateTime as number,
                  )
                }
                countPurchased={countPurchased(item.goods.goodsId)}
              />
            ))}
        </div>
      ) : (
        NoProduct()
      )}
      <UserGuideDialog
        open={isShowUserGuide}
        onClose={() => setIsShowUserGuide(false)}
      />
    </div>
  )
}

export default ProductOrderList
